import { type Address, Hash } from 'ox'
import { useId } from 'react'

import { cx } from './core/classes.js'

/**
 * Deterministic address gradient identicon using design system colors.
 */
export function Identicon(props: Identicon.Props) {
  const { address, className, size = 64 } = props
  const { angle, gradient } = Identicon.getParts(Hash.keccak256(address, { as: 'Bytes' }))
  const id_react = useId()
  const id = `g-${id_react.replace(/[^a-zA-Z0-9_-]/g, '')}-${address.slice(2, 10)}`

  return (
    <svg
      className={cx('rounded-identicon', className)}
      height={size}
      viewBox="0 0 1 1"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient gradientTransform={`rotate(${angle} .5 .5)`} id={id}>
          <stop offset="0%" stopColor={getStopColor(gradient.from)} />
          <stop offset="100%" stopColor={getStopColor(gradient.to)} />
        </linearGradient>
      </defs>
      <rect fill={`url(#${id})`} height="1" width="1" />
    </svg>
  )
}

export namespace Identicon {
  export type Props = {
    /** Tempo address. */
    address: Address.Address
    /** Additional CSS classes. */
    className?: string | undefined
    /** Size in px. @default 64 */
    size?: number | undefined
  }

  /** L/C values based on the palette's colorful `*-7` colors; identity comes from hue. */
  export const lcValues = {
    dark: '68% 0.21',
    light: '66% 0.22',
  } as const

  /** Corner-biased gradient angle ranges. */
  export const angleBands = [
    [25, 65],
    [115, 155],
    [205, 245],
    [295, 335],
  ] as const

  /** Minimum and maximum hue distance between gradient stops. */
  export const hueDelta = [28, 72] as const

  /** Derives deterministic identicon gradient parts from hashed address bytes. */
  export function getParts(hash: Uint8Array) {
    const [minAngle, maxAngle] = angleBands[(hash[2] ?? 0) % angleBands.length] ?? angleBands[0]
    const fromHue = Math.round(((((hash[0] ?? 0) << 8) + (hash[1] ?? 0)) / 65535) * 359)
    const delta = byteToRange(hash[4] ?? 0, hueDelta[0], hueDelta[1])
    const direction = (hash[5] ?? 0) % 2 === 0 ? 1 : -1
    const toHue = (fromHue + direction * delta + 360) % 360
    return {
      angle: byteToRange(hash[3] ?? 0, minAngle, maxAngle),
      gradient: {
        from: fromHue,
        to: toHue,
      },
    }
  }
}

export function getStopColor(hue: number): string {
  return `light-dark(
    oklch(${Identicon.lcValues.light} ${hue}),
    oklch(${Identicon.lcValues.dark} ${hue})
  )`
}

export function byteToRange(byte: number, min: number, max: number): number {
  return min + Math.round((byte / 255) * (max - min))
}
